import React, { useEffect, useRef, useState, useMemo } from "react"
import { 
  UI_BODY_SEMIBOLD_SM, 
  UI_BODY_SM, 
  colors2 
} from "oolib";
import CustomTooltip from "../CustomTooltip";
import { divideBarIntoEqualParts, useGetDimensions } from "./utils/useGetDimensions";
import { usePrepareData } from "./utils/usePrepareData";
import { useTrackMousePosition } from "../utils/useTrackMousePosition";
import { 
  generateYAxisValues, 
  StyledBar, 
  StyledBarGraphCollectionWrapper, 
  StyledBarGraphContainer, 
  StyledBarGraphWrapper, 
  StyledLabelWrapper, 
  StyledSingleBarWrapper, 
  StyledCategoryTitle,
  StyledFadingText,
  YAxisLabels 
} from "./styled";



const { grey5 } = colors2

const useIsOverflowing = (ref, deps = []) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  
  useEffect(() => {
    const el = ref.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [ref, ...deps]);
  
  return isOverflowing;
};

const CategoryTitle = ({ title, width }) => {
  const containerRef = useRef();
  const textRef = useRef();
  const [isOverflowing, setIsOverflowing] = useState(false);
  
  useEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (container && text && title && width) {
      // Create a temporary span to measure natural text width
      const tempSpan = document.createElement('span');
      const textStyle = window.getComputedStyle(text);
      const containerStyle = window.getComputedStyle(container);
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.fontSize = textStyle.fontSize;
      tempSpan.style.fontFamily = textStyle.fontFamily;
      tempSpan.style.fontWeight = textStyle.fontWeight;
      tempSpan.style.letterSpacing = textStyle.letterSpacing;
      tempSpan.textContent = title;
      document.body.appendChild(tempSpan);
      const textWidth = tempSpan.getBoundingClientRect().width;
      document.body.removeChild(tempSpan);
      
      const containerPadding = parseFloat(containerStyle.paddingLeft) + parseFloat(containerStyle.paddingRight);
      const availableWidth = width - containerPadding;
      setIsOverflowing(textWidth > availableWidth);
    }
  }, [title, width]);
  
  return (
    <StyledCategoryTitle
      ref={containerRef}
      title={title}
      style={{ width: `${width}px` }}
    >
      <UI_BODY_SM>
        <StyledFadingText ref={textRef} $isOverflowing={isOverflowing}>
          {title}
        </StyledFadingText>
      </UI_BODY_SM>
    </StyledCategoryTitle>
  );
};

const BarLabel = ({ display, maxWidth, onOverflowChange }) => {
  const containerRef = useRef();
  const textRef = useRef();
  
  useEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (container && text && display && maxWidth) {
      // Create a temporary span to measure natural text width
      const tempSpan = document.createElement('span');
      const textStyle = window.getComputedStyle(text);
      const containerStyle = window.getComputedStyle(container);
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.fontSize = textStyle.fontSize;
      tempSpan.style.fontFamily = textStyle.fontFamily;
      tempSpan.style.fontWeight = textStyle.fontWeight;
      tempSpan.style.letterSpacing = textStyle.letterSpacing;
      tempSpan.textContent = display;
      document.body.appendChild(tempSpan);
      const textWidth = tempSpan.getBoundingClientRect().width;
      document.body.removeChild(tempSpan);
      
      const containerPadding = parseFloat(containerStyle.paddingLeft) + parseFloat(containerStyle.paddingRight);
      const availableWidth = maxWidth - containerPadding;
      onOverflowChange?.(textWidth > availableWidth);
    }
  }, [display, maxWidth, onOverflowChange]);
  
  return (
    <StyledLabelWrapper ref={containerRef} $maxWidth={maxWidth}>
      <UI_BODY_SM>
        <StyledFadingText ref={textRef}>
          {display}
        </StyledFadingText>
      </UI_BODY_SM>
    </StyledLabelWrapper>
  );
};

const SingleBar = ({ tag, debug, isHovered, mousePosition, tooltipData, showPercent, onMouseEnter, onMouseLeave, onClick }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <StyledSingleBarWrapper barSections={tag.barSections} debug={debug} $isOverflowing={isOverflowing}>
        <StyledBar
          style={{
            height: `${tag.height}px`,
            width: `${tag.width}px`
          }}
        />
        <BarLabel display={tag?.display} maxWidth={BAR_GRAPH_HEIGHT} onOverflowChange={setIsOverflowing} />

        {isHovered && mousePosition && (
          <CustomTooltip
            active={true}
            payload={[tag]}
            mousePosition={mousePosition}
            value={tooltipData}
            showPercent={showPercent}
          />
        )}
      </StyledSingleBarWrapper>
    </div>
  );
};

export const BAR_GRAPH_HEIGHT = 250;
export const DEFAULT_SINGLE_BAR_WIDTH = 24;
export const BAR_GAP = 0.3;

export const BarGraphCollection = ({ 
  data: originalData,
  valuePath,
  labelPath = "display",
  tooltipLabelsMapping = {},
  onClick,
  showCount = true,
  showPercent = true,
  groupBy,
  totalDataCount,
  debug

  // tooltipLabelsPath,
  // colorIdx = 0,
  // summarizeAfterIdx = 5,
  // breakdownDisplayType = "grouped"
}) => {
  const containerRef = useRef();
  const [ containerWidth, setContainerWidth ] = useState(0);
  
  const highestCount = originalData?.reduce(( highest, item ) =>  item.value > highest ? item.value : highest , 0)

  const transformedData = useMemo(() => {
    return usePrepareData({ data: originalData, valuePath, labelPath, tooltipLabelsMapping, groupBy });
  }, [originalData, valuePath, labelPath, tooltipLabelsMapping, groupBy]);
  
  const data = useGetDimensions(transformedData, BAR_GRAPH_HEIGHT, containerWidth, totalDataCount, highestCount);

  
  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(containerRef?.current?.getBoundingClientRect()?.width)
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    
    return () => window.removeEventListener("resize", handleResize);
  }, [transformedData])
  
  if(data?.length === 0){
    return null
  }
  const barDivisions = divideBarIntoEqualParts(highestCount)

  return (
    <StyledBarGraphContainer style={{ width: `auto` }} ref={containerRef}> 
      <div style={{ position: "absolute", left: 20, height: `${BAR_GRAPH_HEIGHT}px`, width: "20px"}}>
        <YAxisLabels>
            {generateYAxisValues(barDivisions).map((value, index) => (
              <UI_BODY_SM key={index}>{Intl.NumberFormat('en', { notation: 'compact' }).format(value)}</UI_BODY_SM>
            ))}
          </YAxisLabels>
      </div>
      <StyledBarGraphCollectionWrapper
        style={{
          width: `auto`,
          gap: `${BAR_GAP}rem`,
          overflowX: "auto"
        }}>
        {data?.map((tagCategory, index1) => {
          const categoryTitle = tagCategory?.tagCategory ? (tagCategory.tagCategory.charAt(0).toUpperCase() + tagCategory.tagCategory.slice(1)) : undefined

          return (
            <div key={`${index1}-${tagCategory.tagCategory}`} 
                 style={{ 
                   display: "flex", 
                   flexDirection: "column", 
                   gap: `${BAR_GAP}rem`, 
                   // height: `${BAR_GRAPH_HEIGHT + 50}px`,
                   marginBottom: "0.6rem" 
                 }}>
              <BarGraph 
                tagCategory={tagCategory} 
                index1={index1} 
                showCount={showCount}
                showPercent={showPercent}
                onClick={onClick}
                debug={debug}
                highestCount={highestCount}
              />

              {categoryTitle && <CategoryTitle title={categoryTitle} width={tagCategory?.width} />}

            </div>
          )
        })}
      </StyledBarGraphCollectionWrapper>
    </StyledBarGraphContainer>
  );
}

export const BarGraph = ({ 
  tagCategory, 
  index1, 
  showCount = true,
  showPercent = true,
  onClick,
  debug,
}) => {
  const containerRef = useRef()
  const { mousePosition, isHovering } = useTrackMousePosition({ containerRef })
  const [hoveredBarIndex, setHoveredBarIndex] = useState(null);

  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  const handleBarMouseEnter = (index) => {
    setHoveredBarIndex(index);
  };

  const handleBarMouseLeave = () => {
    setHoveredBarIndex(null);
  };

  const handleBarClick = (tag) => {
    if (onClick) {
      onClick(tag.display);
    }
  };

  return (
    <StyledBarGraphWrapper
      ref={containerRef}
      style={{
        height: `${BAR_GRAPH_HEIGHT}px`,
        gap: `${BAR_GAP}rem`,
      }}>
      {tagCategory?.tagSpecificCount?.map((tag, index) => {

        const tooltipData = [{
          percentage: showPercent ? tag.percentage : undefined,
          count: showCount ? tag.count : undefined,
          tooltipLabel: tag.display
        }];
        
        const isHovered = isHovering && hoveredBarIndex === index;
        
        const stableKey = `${tagCategory?.tagCategory || 'cat'}-${tag.tagId || tag.display || index}`;

        return(
          <SingleBar
            key={stableKey}
            tag={tag}
            debug={debug}
            isHovered={isHovered}
            mousePosition={mousePosition}
            tooltipData={tooltipData}
            showPercent={showPercent}
            onMouseEnter={() => handleBarMouseEnter(index)}
            onMouseLeave={handleBarMouseLeave}
            onClick={() => handleBarClick(tag)}
          />
        )
      })
      }
    </StyledBarGraphWrapper>
  )
}
